import Box from '@app/components/Box';
import DevText from '@app/components/Text';
import { Colors, Icon, Intent, ProgressBar } from '@blueprintjs/core';
import React, { HTMLAttributes } from 'react';
import { OccupancyBand, formatOccupancyPercentage, getOccupancyBand } from './utils';
import { BandProgressBarWrapper } from './styled';

export interface OccupancyRateProps {
    interactive?: boolean;
    score: number;

    // Opt in to coloring the percentage and the bar fill by utilization band instead of the legacy
    // low/medium/high intents. Off by default, so existing callers keep the colors they have today.
    colorByBand?: boolean;
}
export type Props = HTMLAttributes<HTMLDivElement> & OccupancyRateProps;

const OCCUPANCY_BAND_COLORS: Record<OccupancyBand, string> = {
    under: Colors.GRAY4,
    optimal: Colors.GREEN4,
    nearCapacity: Colors.ORANGE4,
    overloaded: Colors.RED4,
};

// Band color for a score, exported so a caller can color a surrounding heading to match its bars
export const getOccupancyBandColor = (score: number) => OCCUPANCY_BAND_COLORS[getOccupancyBand(score)];

// Calculate percentage from total
// NOTE: the formatting itself lives in utils, so the number shown here and the band that colors it
// NOTE: round exactly once and cannot disagree
export const calculatePercentage = formatOccupancyPercentage;

const OccupancyRate: React.FC<Props> = ({ interactive = false, colorByBand = false, score, ...props }) => {
    // Get color & intent information
    let scoreColor = Colors.GREEN4;
    let occupancyRateIntent: Intent = Intent.SUCCESS;
    if (score > 0.49 && score < 0.8) {
        // Occupancy is medium
        occupancyRateIntent = Intent.WARNING;
        scoreColor = Colors.ORANGE4;
    } else if (score > 0.79) {
        // Occupancy is high
        occupancyRateIntent = Intent.DANGER;
        scoreColor = Colors.RED4;
    }

    if (colorByBand) {
        // Band mode ignores the legacy thresholds and colors from the displayed percentage instead
        scoreColor = getOccupancyBandColor(score);
    }

    const renderOccupancyOverloadedInformation = () => {
        if (score < 1.01) {
            return null;
        }

        return <Icon className="mr-small" icon="warning-sign" color={scoreColor} />;
    };

    const renderProgressBar = () => {
        if (colorByBand) {
            return (
                <BandProgressBarWrapper $fillColor={scoreColor}>
                    <ProgressBar animate={false} stripes={false} value={score} />
                </BandProgressBarWrapper>
            );
        }

        return <ProgressBar animate={false} stripes={false} intent={occupancyRateIntent} value={score} />;
    };

    return (
        <Box cursor={interactive ? 'pointer' : 'default'} {...props}>
            <DevText className="mb-1" color={scoreColor}>
                {renderOccupancyOverloadedInformation()}
                {calculatePercentage(score)}%
            </DevText>
            {renderProgressBar()}
        </Box>
    );
};

export default OccupancyRate;
